// TopWallet — Individual article page
// Route: /topwallet/:seccion/:slug
import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, AlertTriangle, Lightbulb, Sparkles } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { getTopWalletI18n } from "../i18n/topwallet";
import { getArticuloPorSlug } from "../data/topwalletArticulos";
import { SITE_URL, OG_IMAGE } from "../i18n/seo_routes";
import LedgerAffiliateCTA from "../components/topwallet/LedgerAffiliateCTA";
import TrezorAffiliateCTA from "../components/topwallet/TrezorAffiliateCTA";
import { i18nTrezor, pickLang as pickTrezorLang } from "../i18n/trezor-affiliate";

// Affiliate CTA mapping per article slug → tracker + variant.
const LEDGER_CTA_MAP = {
  "hardware-wallets-comparativa": { tracker: "topwallet_hardware", variant: "full" },
  "guia-completa-seguridad": { tracker: "topwallet_seguridad", variant: "full" },
  "que-es-una-wallet": { tracker: "topwallet_carteras", variant: "compact" },
  "custodial-vs-self-custody": { tracker: "topwallet_custodial", variant: "full" },
};

// Trezor mirrors the Ledger mapping (same articles, same variants, own trackers).
const TREZOR_CTA_MAP = {
  "hardware-wallets-comparativa": { tracker: "topwallet_hardware", variant: "full" },
  "guia-completa-seguridad": { tracker: "topwallet_seguridad", variant: "full" },
  "que-es-una-wallet": { tracker: "topwallet_carteras", variant: "compact" },
  "custodial-vs-self-custody": { tracker: "topwallet_custodial", variant: "full" },
};

const HREF_MAP = { es: "es-ES", en: "en-US", sk: "sk-SK", de: "de-DE", "zh-CN": "zh-CN" };

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href, attrs = {}) => {
  let sel = `link[rel="${rel}"]`;
  Object.entries(attrs).forEach(([k, v]) => (sel += `[${k}="${v}"]`));
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const NIVEL_STYLES = {
  basico: { border: "rgba(0,230,118,.5)", bg: "rgba(0,230,118,.12)", text: "#00e676" },
  intermedio: { border: "rgba(245,166,35,.5)", bg: "rgba(245,166,35,.12)", text: "#f5a623" },
  avanzado: { border: "rgba(255,51,85,.5)", bg: "rgba(255,51,85,.12)", text: "#ff3355" },
};

const TopWalletArticuloPage = () => {
  const { seccion, slug } = useParams();
  const { lang } = useLanguage();
  const ui = useMemo(() => getTopWalletI18n(lang), [lang]);
  const article = useMemo(() => getArticuloPorSlug(slug, lang), [slug, lang]);

  // Word count for schema (only when article is valid)
  const wordCount = useMemo(() => {
    if (!article) return 0;
    let n = (article.titulo?.split(/\s+/).length || 0) + (article.resumen?.split(/\s+/).length || 0);
    for (const sec of article.cuerpo || []) {
      n += sec.subtitulo?.split(/\s+/).length || 0;
      n += sec.texto?.split(/\s+/).length || 0;
    }
    for (const p of article.puntosClave || []) n += p.split(/\s+/).length;
    if (article.advertencia) n += article.advertencia.split(/\s+/).length;
    if (article.tip) n += article.tip.split(/\s+/).length;
    return n;
  }, [article]);

  useEffect(() => {
    if (!article || article.seccion !== seccion) return;
    const url = `${SITE_URL}/topwallet/${seccion}/${slug}`;
    const hreflang = HREF_MAP[lang] || "es-ES";

    document.title = `${article.titulo} · TopWallet · RE₿EL`;
    document.documentElement.lang = lang;

    setMeta('meta[name="description"]', "content", article.resumen);
    setMeta('meta[name="author"]', "content", "TopWallet — Academia RE₿EL");
    setMeta(
      'meta[name="robots"]',
      "content",
      "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );

    setLink("canonical", url);
    setLink("alternate", `${url}?lang=es`, { hreflang: "es-ES" });
    setLink("alternate", `${url}?lang=en`, { hreflang: "en-US" });
    setLink("alternate", `${url}?lang=sk`, { hreflang: "sk-SK" });
    setLink("alternate", url, { hreflang: "x-default" });

    // OG / Twitter
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:title"]', "content", article.titulo);
    setMeta('meta[property="og:description"]', "content", article.resumen);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", OG_IMAGE);
    setMeta('meta[property="og:locale"]', "content", hreflang.replace("-", "_"));
    setMeta('meta[property="article:section"]', "content", seccion);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", article.titulo);
    setMeta('meta[name="twitter:description"]', "content", article.resumen);
    setMeta('meta[name="twitter:image"]', "content", OG_IMAGE);

    const sectionCardLabel = ui.cards.find((c) => c.id === seccion)?.titulo || seccion;

    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${url}#article`,
          headline: article.titulo,
          description: article.resumen,
          image: [OG_IMAGE],
          inLanguage: hreflang,
          articleSection: sectionCardLabel,
          wordCount,
          timeRequired: article.tiempoLectura,
          educationalLevel: article.nivel,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          author: {
            "@type": "Organization",
            name: "TopWallet — Academia RE₿EL",
            url: `${SITE_URL}/#topwallet`,
          },
          publisher: {
            "@type": "Organization",
            name: "RE₿EL BTC",
            url: SITE_URL,
            logo: { "@type": "ImageObject", url: OG_IMAGE },
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "RE₿EL BTC", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "TopWallet", item: `${SITE_URL}/#topwallet` },
            { "@type": "ListItem", position: 3, name: sectionCardLabel, item: `${SITE_URL}/topwallet/${seccion}` },
            { "@type": "ListItem", position: 4, name: article.titulo, item: url },
          ],
        },
      ],
    };
    setJsonLd("rebel-jsonld", graph);
  }, [article, seccion, slug, lang, wordCount, ui]);

  if (!article || article.seccion !== seccion) {
    return <Navigate to={`/topwallet/${seccion}`} replace />;
  }

  const sectionCard = ui.cards.find((c) => c.id === seccion);
  const nivelStyle = NIVEL_STYLES[article.nivel] || NIVEL_STYLES.basico;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_60%,rgba(245,166,35,0.10),transparent_55%)]" />
      </div>

      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to={`/topwallet/${seccion}`}
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="articulo-back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.volverSeccion}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 py-10 md:px-10 md:py-14">
        {/* Breadcrumb */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
          <Link to="/#topwallet" className="hover:text-cyan-300">{ui.breadcrumbRoot}</Link>
          <span className="text-white/30">→</span>
          <Link to={`/topwallet/${seccion}`} className="hover:text-cyan-300">{sectionCard?.titulo}</Link>
          <span className="text-white/30">→</span>
          <span className="text-cyan-200">{article.titulo}</span>
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="mb-4 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.22em]">
            <span
              className="rounded-full px-2.5 py-1 font-black"
              style={{
                border: `1px solid ${nivelStyle.border}`,
                background: nivelStyle.bg,
                color: nivelStyle.text,
              }}
              data-testid="articulo-nivel"
            >
              {ui.nivelBadge[article.nivel]}
            </span>
            <span className="inline-flex items-center gap-1.5 text-white/55">
              <Clock className="h-3 w-3" />
              {article.tiempoLectura}
            </span>
          </div>
          <h1
            data-testid="articulo-title"
            className="font-display text-3xl font-black leading-tight tracking-tight md:text-5xl"
          >
            <span className="mr-2 align-middle">{article.icono}</span>
            {article.titulo}
          </h1>
        </motion.div>

        {/* Resumen */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mb-10 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-5 md:p-6"
          data-testid="articulo-resumen"
        >
          <h2 className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300">
            {ui.resumenLabel}
          </h2>
          <p className="text-base leading-relaxed text-white/80 md:text-lg">{article.resumen}</p>
        </motion.div>

        {/* Cuerpo */}
        <div className="space-y-10">
          {article.cuerpo.map((sec, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: 0.05 * i }}
              data-testid={`articulo-seccion-${i}`}
            >
              <h2 className="mb-3 font-display text-xl font-bold text-white md:text-2xl">
                {sec.subtitulo}
              </h2>
              <p className="whitespace-pre-line text-[15px] leading-[1.8] text-white/70 md:text-base">
                {sec.texto}
              </p>
            </motion.section>
          ))}
        </div>

        {/* Puntos clave */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 rounded-2xl border border-amber-400/25 bg-amber-400/[0.05] p-5 md:p-6"
          data-testid="articulo-puntos-clave"
        >
          <h2 className="mb-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-amber-300">
            <Sparkles className="h-3.5 w-3.5" />
            {ui.puntosClaveLabel}
          </h2>
          <ul className="space-y-2">
            {article.puntosClave.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-white/75 md:text-base">
                <span className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-300" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Advertencia */}
        {article.advertencia && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-5 md:p-6"
            data-testid="articulo-advertencia"
          >
            <h2 className="mb-2 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-rose-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              {ui.advertenciaLabel}
            </h2>
            <p className="text-sm leading-relaxed text-white/80 md:text-base">{article.advertencia}</p>
          </motion.div>
        )}

        {/* Tip */}
        {article.tip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] p-5 md:p-6"
            data-testid="articulo-tip"
          >
            <h2 className="mb-2 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-emerald-300">
              <Lightbulb className="h-3.5 w-3.5" />
              {ui.tipLabel}
            </h2>
            <p className="text-sm leading-relaxed text-white/80 md:text-base">{article.tip}</p>
          </motion.div>
        )}

        {/* Ledger affiliate CTA (per-article) */}
        {LEDGER_CTA_MAP[slug] && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55 }}
          >
            <LedgerAffiliateCTA
              lang={lang}
              tracker={LEDGER_CTA_MAP[slug].tracker}
              variant={LEDGER_CTA_MAP[slug].variant}
            />
          </motion.div>
        )}

        {/* Trezor affiliate CTA (per-article — additive, sibling of Ledger) */}
        {TREZOR_CTA_MAP[slug] && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            {/* "O TAMBIÉN" separator (only when Ledger is also shown above) */}
            {LEDGER_CTA_MAP[slug] && (
              <div
                aria-hidden="true"
                data-testid={`trezor-cta-separator-${slug}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "20px 0",
                  color: "rgba(255,255,255,.2)",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  fontFamily: "'Courier New', monospace",
                }}
              >
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.08)" }} />
                <span>{pickTrezorLang(i18nTrezor.oTambien, lang)}</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.08)" }} />
              </div>
            )}
            <TrezorAffiliateCTA
              lang={lang}
              tracker={TREZOR_CTA_MAP[slug].tracker}
              variant={TREZOR_CTA_MAP[slug].variant}
            />
          </motion.div>
        )}

        {/* CTAs */}
        <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to={`/topwallet/${seccion}`}
            data-testid="articulo-cta-back"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white/75 transition-colors hover:border-cyan-400/40 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {ui.volverSeccion}
          </Link>
          <Link
            to="/topwallet/comparador"
            data-testid="articulo-cta-comparador"
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-[#07080f] shadow-[0_4px_16px_rgba(245,166,35,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_28px_rgba(245,166,35,0.5)]"
            style={{ background: "linear-gradient(135deg, #f5a623, #e8941a)" }}
          >
            {ui.comparadorBtn}
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TopWalletArticuloPage;
